import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { loadStoryForCode } from "../lib/firestore";
import type { ViewerState, StoryItem } from "../lib/types";
import { PhotoCarousel } from "../components/PhotoCarousel";
import { VideoPlayer } from "../components/VideoPlayer";
import {
  DraftState,
  ErrorState,
  LoadingState,
  MissingState,
  PrivateState,
  UnassignedState,
} from "../components/States";

// /q/:code — the only route the viewer serves. Reads the QR + story from
// Firestore (anonymously) and dispatches to the appropriate state.

export default function Story() {
  const { code = "" } = useParams<{ code: string }>();
  const [state, setState] = useState<ViewerState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    loadStoryForCode(code).then((next) => {
      if (!cancelled) setState(next);
    });
    return () => {
      cancelled = true;
    };
  }, [code]);

  // Update the document title once we know what we're showing — useful for
  // browser tabs and shared screenshots.
  useEffect(() => {
    if (state.kind === "story") {
      document.title = `${state.story.title} · Story Stamp`;
    } else {
      document.title = "Story Stamp";
    }
  }, [state]);

  switch (state.kind) {
    case "loading":    return <LoadingState />;
    case "missing":    return <MissingState code={state.code} />;
    case "unassigned": return <UnassignedState />;
    case "draft":      return <DraftState />;
    case "private":    return <PrivateState />;
    case "error":      return <ErrorState message={state.message} />;
    case "locked":     return <LockedStory story={state.story} />;
    case "story":      return <PublishedStory story={state.story} />;
  }
}

// MARK: - Locked Story
// Password gate for private stories. Verifies the password client-side using
// PBKDF2-SHA256 (Web Crypto API) — the same algorithm used on iOS. The plain-
// text password never leaves the browser; only the derived key is compared to
// the stored hash. On correct entry the full story fades in.

async function pbkdf2(password: string, saltHex: string): Promise<string> {
  const encoder = new TextEncoder();
  const saltBytes = new Uint8Array(
    (saltHex.match(/.{1,2}/g) ?? []).map((b) => parseInt(b, 16))
  );
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    encoder.encode(password),
    "PBKDF2",
    false,
    ["deriveBits"]
  );
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", hash: "SHA-256", salt: saltBytes, iterations: 100_000 },
    keyMaterial,
    256
  );
  return Array.from(new Uint8Array(bits))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function LockedStory({ story }: { story: StoryItem }) {
  const [password, setPassword] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [wrongPassword, setWrongPassword] = useState(false);
  const [unlocked, setUnlocked] = useState(false);

  const handleUnlock = useCallback(async () => {
    const trimmed = password.trim();
    if (!trimmed || isVerifying) return;

    setIsVerifying(true);
    setWrongPassword(false);

    try {
      const derived = await pbkdf2(trimmed, story.passwordSalt ?? "");
      if (derived === story.passwordHash) {
        setUnlocked(true);
      } else {
        setWrongPassword(true);
        setPassword("");
      }
    } catch {
      setWrongPassword(true);
      setPassword("");
    } finally {
      setIsVerifying(false);
    }
  }, [password, isVerifying, story]);

  if (unlocked) return <PublishedStory story={story} />;

  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center gap-6 px-6 text-center">
      {/* Lock icon */}
      <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
        <svg
          className="w-9 h-9 text-primary"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            fillRule="evenodd"
            d="M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3v-6.75a3 3 0 00-3-3v-3A5.25 5.25 0 0012 1.5zm3.75 8.25v-3a3.75 3.75 0 10-7.5 0v3h7.5z"
            clipRule="evenodd"
          />
        </svg>
      </div>

      {/* Title */}
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold text-textPrimary">{story.title}</h1>
        <p className="text-textSecondary text-sm max-w-xs">
          This story is private. Enter the password to view it.
        </p>
      </div>

      {/* Password form */}
      <div className="w-full max-w-sm space-y-3">
        <input
          type="password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setWrongPassword(false);
          }}
          onKeyDown={(e) => e.key === "Enter" && handleUnlock()}
          placeholder="Password"
          className={`w-full px-4 py-3 rounded-xl border bg-surface text-textPrimary placeholder:text-textTertiary outline-none focus:ring-2 focus:ring-primary/40 transition ${
            wrongPassword ? "border-red-400" : "border-border"
          }`}
          autoFocus
        />

        {wrongPassword && (
          <p className="text-red-500 text-sm">Incorrect password. Please try again.</p>
        )}

        <button
          onClick={handleUnlock}
          disabled={!password.trim() || isVerifying}
          className="w-full py-3 rounded-xl bg-primary text-white font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {isVerifying ? "Verifying…" : "Unlock Story"}
        </button>
      </div>

      <footer className="mt-4 text-xs text-textTertiary">
        <a href="https://storystamp.com" className="text-primary font-medium hover:underline">
          Get your own Story Stamp →
        </a>
      </footer>
    </div>
  );
}

function PublishedStory({ story }: { story: StoryItem }) {
  const dateLine = useMemo(() => formatUpdated(story), [story]);

  return (
    <article className="mx-auto max-w-story pb-20">
      {story.photoURLs.length > 0 && <PhotoCarousel urls={story.photoURLs} />}

      <div className="px-5 pt-6 pb-4">
        <CategoryBadge category={story.category} />
        <h1 className="mt-3 text-3xl font-semibold tracking-tight text-textPrimary">
          {story.title}
        </h1>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-textTertiary">
          {story.estimatedValue != null && (
            <span className="text-accent font-medium">
              Est. {formatCurrency(story.estimatedValue)}
            </span>
          )}
          {dateLine && <span>{dateLine}</span>}
        </div>
      </div>

      <div className="px-5">
        <hr className="border-border" />
      </div>

      <div className="px-5 pt-6">
        <h2 className="text-sm uppercase tracking-wider text-textTertiary mb-3">
          The Story
        </h2>
        <p className="story-prose font-serif text-textPrimary">
          {story.storyText}
        </p>
      </div>

      {story.videoURL && (
        <div className="px-5 pt-8">
          <h2 className="text-sm uppercase tracking-wider text-textTertiary mb-3">
            Video
          </h2>
          <VideoPlayer src={story.videoURL} />
        </div>
      )}

      <Footer />
    </article>
  );
}

function CategoryBadge({ category }: { category: string }) {
  return (
    <span className="inline-block bg-primary/10 text-primary text-xs font-medium uppercase tracking-wider px-3 py-1 rounded-full">
      {category}
    </span>
  );
}

function Footer() {
  return (
    <footer className="mt-16 px-5 pt-8 border-t border-border text-center text-textTertiary text-xs">
      <a
        href="https://storystamp.com"
        className="text-primary font-medium hover:underline"
      >
        Get your own Story Stamp →
      </a>
      <p className="mt-3">Every Stamp, a Story.</p>
    </footer>
  );
}

function formatUpdated(story: StoryItem): string | null {
  const ts = story.updatedAt ?? story.createdAt;
  if (!ts) return null;
  const date = ts.toDate();
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}
